import { useState, useRef, useCallback } from "react";

/**
 * Hook for screen recording using getDisplayMedia API.
 * Records the screen and downloads the recording as a .webm file when stopped.
 */
export const useScreenRecording = () => {
  const [isScreenRecording, setIsScreenRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);

  const startScreenRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "video/webm; codecs=vp8,opus",
      });

      // Formats date as YYYY-MM-DD
      const date = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: "video/webm",
        });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `screen-recording-${date}.webm`;
        a.click();

        URL.revokeObjectURL(url);
        recordedChunksRef.current = [];

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      // Handle user stopping screen share via browser UI
      stream.getVideoTracks()[0].onended = () => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
        }
        setIsScreenRecording(false);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsScreenRecording(true);
    } catch (err) {
      console.error("Error starting screen recording:", err);
    }
  }, []);

  const stopScreenRecording = useCallback(() => {
    if (mediaRecorderRef.current && isScreenRecording) {
      mediaRecorderRef.current.stop();
      setIsScreenRecording(false);
    }
  }, [isScreenRecording]);

  const handleScreenRecording = useCallback(() => {
    if (isScreenRecording) {
      stopScreenRecording();
    } else {
      startScreenRecording();
    }
  }, [isScreenRecording, startScreenRecording, stopScreenRecording]);

  return { isScreenRecording, handleScreenRecording };
};
